import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import AdminModal from '../common/AdminModal';
import FormInput from '../../../shared/components/FormInput';
import { getIntegrationManifest } from '../../../integrations';

function buildInitialFields(integrationType) {
  const values = {};
  (integrationType?.setup_fields || []).forEach((field) => {
    values[field.key] = field.default ?? '';
  });
  return values;
}

function buildInitialCredentials(integrationType) {
  const values = {};
  (integrationType?.setup_schema || []).forEach((field) => {
    values[field.key] = '';
  });
  return values;
}

export default function IntegrationSetupModal({ show, integrationType, saving, onClose, onCreate }) {
  const [fields, setFields] = useState({});
  const [credentials, setCredentials] = useState({});
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!show || !integrationType) return;
    setFields(buildInitialFields(integrationType));
    setCredentials(buildInitialCredentials(integrationType));
    setFormError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- integrationType?.type is sufficient trigger
  }, [show, integrationType?.type]);

  if (!integrationType) return null;

  const manifest = getIntegrationManifest(integrationType.type);
  const title = integrationType.display_name || manifest?.displayName || 'Set up integration';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    try {
      await onCreate({
        type: integrationType.type,
        fields,
        credentials,
      });
      onClose();
    } catch (err) {
      setFormError(err.message || 'Setup failed');
    }
  };

  return (
    <AdminModal
      show={show}
      onClose={onClose}
      title={`Set up ${title}`}
      footer={(
        <div className="d-flex justify-content-between w-100">
          <button type="button" className="btn btn-link text-muted" onClick={onClose} disabled={saving}>
            <i className="bi bi-arrow-left me-1" /> Cancel
          </button>
          <button type="submit" form="integration-setup-form" className="btn btn-primary" disabled={saving}>
            {saving ? 'Creating…' : 'Create integration'}
          </button>
        </div>
      )}
    >
      <form id="integration-setup-form" onSubmit={handleSubmit} className="d-flex flex-column gap-3">
        {formError && (
          <div className="alert alert-danger py-2 mb-0" role="alert">
            {formError}
          </div>
        )}

        {(integrationType.setup_fields || []).map((field) => (
          <FormInput
            key={field.key}
            id={`setup-field-${field.key}`}
            label={field.label}
            type={field.type === 'url' ? 'url' : 'text'}
            value={fields[field.key] ?? ''}
            onChange={(e) => setFields((prev) => ({ ...prev, [field.key]: e.target.value }))}
            required={field.required}
          />
        ))}

        {(integrationType.setup_schema || []).length > 0 && (
          <div>
            <h6 className="text-muted fw-normal mb-2">Credentials</h6>
            {(integrationType.setup_schema || []).map((field) => (
              <FormInput
                key={field.key}
                id={`setup-credential-${field.key}`}
                label={field.label}
                type="password"
                value={credentials[field.key] ?? ''}
                onChange={(e) => setCredentials((prev) => ({ ...prev, [field.key]: e.target.value }))}
                required={field.required}
                className="mb-3"
              />
            ))}
          </div>
        )}
      </form>
    </AdminModal>
  );
}

IntegrationSetupModal.propTypes = {
  show: PropTypes.bool.isRequired,
  integrationType: PropTypes.object,
  saving: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
};
